import React from 'react';

import { useManagementPackContext } from './ManagementPackContext';
import { fillUrl } from './managementPackUtils';

import { formatDateLong } from 'Helpers/dateFormat';

import Badge from 'CommonComponents/Badge';
import TextButton from 'CommonComponents/TextButton';

const PackActions = ({ pack }) => {
    const { urls } = useManagementPackContext();

    return (
        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-3">
            <TextButton as="a" href={pack.urls?.builder} aria-label={$t('Open {{period}}', { period: pack.periodLabel })}>
                {$t('Open')}
            </TextButton>
            {pack.isIssued && pack.latestExportId && (
                <TextButton
                    as="a"
                    href={fillUrl(urls.exportDownload, { id: pack.latestExportId })}
                    aria-label={$t('Download {{period}}', { period: pack.periodLabel })}
                >
                    {$t('Download')}
                </TextButton>
            )}
        </div>
    );
};

const issuedDetails = (pack) => {
    if (!pack.issuedAt) {
        return null;
    }

    const issued = pack.issuedBy
        ? $t('Issued {{date}} by {{name}}', { date: formatDateLong(pack.issuedAt), name: pack.issuedBy })
        : $t('Issued {{date}}', { date: formatDateLong(pack.issuedAt) });

    return pack.issuedTo ? $t('{{issued}} to {{recipient}}', { issued, recipient: pack.issuedTo }) : issued;
};

const packDetails = (pack, setName) =>
    [setName || $t('Custom configuration'), pack.generatedAt ? $t('generated {{date}}', { date: formatDateLong(pack.generatedAt) }) : null]
        .filter(Boolean)
        .join(' · ');

/**
 * The packs this user can see for the event, newest period first.
 */
const GeneratedPacksList = () => {
    const { packs, savedSets } = useManagementPackContext();

    const setNames = new Map(savedSets.map((set) => [set.id, set.name]));

    return (
        <div className="tw-pt-5">
            <h3 className="tw-text-xs tw-font-semibold tw-text-charcoal-75 tw-m-0 tw-pb-2">{$t('Generated packs')}</h3>

            {packs.length ? (
                <ul className="tw-list-none tw-m-0 tw-p-0">
                    {packs.map((pack) => (
                        <li
                            key={pack.id}
                            className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-3 tw-border-b tw-border-charcoal-15 last:tw-border-b-0"
                        >
                            <div className="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0">
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <span className="tw-text-sm tw-font-semibold tw-text-charcoal">{pack.periodLabel}</span>
                                    {pack.isIssued ? <Badge variant="success">{$t('Issued')}</Badge> : <Badge variant="info">{$t('Draft')}</Badge>}
                                    {pack.isSuperseded && <Badge variant="warning">{$t('Superseded')}</Badge>}
                                    {pack.revision > 1 && <Badge variant="neutral">{$t('Revision {{revision}}', { revision: pack.revision })}</Badge>}
                                </div>
                                <span className="tw-text-xs tw-text-charcoal-75 tw-truncate" title={packDetails(pack, setNames.get(pack.reportFilterSetId))}>
                                    {packDetails(pack, setNames.get(pack.reportFilterSetId))}
                                </span>
                                {pack.issuedAt && <span className="tw-text-xs tw-text-charcoal-75 tw-break-words">{issuedDetails(pack)}</span>}
                            </div>
                            <PackActions pack={pack} />
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="tw-text-sm tw-text-charcoal-75 tw-m-0">{$t('No packs have been generated yet.')}</p>
            )}
        </div>
    );
};

export default GeneratedPacksList;
